import React from 'react';
import {
    Clock, TrendingUp, TrendingDown,
    AlertCircle, CheckCircle2, Zap
} from 'lucide-react';

export default function WeeklyOpsReport({ summary, onClose }) {
    if (!summary) return null;

    return (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
            <div className="bg-[#0d0d0f] border border-slate-800 w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
                <div className="p-6 border-b border-slate-800 flex justify-between items-center bg-[#0a0a0c]">
                    <div>
                        <h2 className="text-lg font-black text-white tracking-tight flex items-center gap-2">
                            <Clock className="w-5 h-5 text-indigo-500" />
                            Weekly Decision Summary
                        </h2>
                        <p className="text-xs text-slate-500 font-medium uppercase tracking-widest mt-1">Ops Pulse Report</p>
                    </div>
                    <button 
                        onClick={onClose}
                        className="text-slate-500 hover:text-white transition-colors"
                    >
                        Close
                    </button>
                </div>

                <div className="p-8 overflow-y-auto space-y-8 custom-scrollbar">
                    {/* Key Movements */}
                    <div className="grid grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Active Workspaces</div>
                            <div className="flex items-end gap-3">
                                <span className="text-3xl font-black text-white">{summary.activeWorkspaces.current}</span>
                                <div className={`flex items-center gap-1 text-sm font-bold pb-1 ${
                                    summary.activeWorkspaces.change >= 0 ? 'text-emerald-400' : 'text-rose-400'
                                }`}>
                                    {summary.activeWorkspaces.change >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                                    {summary.activeWorkspaces.change}%
                                </div>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest">New Workspaces</div>
                            <div className="flex items-end gap-3">
                                <span className="text-3xl font-black text-white">{summary.newWorkspaces.current}</span>
                                <div className={`flex items-center gap-1 text-sm font-bold pb-1 ${
                                    summary.newWorkspaces.change >= 0 ? 'text-emerald-400' : 'text-rose-400'
                                }`}>
                                    {summary.newWorkspaces.change >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                                    {summary.newWorkspaces.change}%
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Dynamic Insights & Observations */}
                    <div className="space-y-4">
                        <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                            <AlertCircle className="w-4 h-4" />
                            Decision Insights
                        </h3>
                        <div className="space-y-3">
                            {summary.insights && summary.insights.length > 0 ? (
                                summary.insights.map((insight, idx) => (
                                    <div key={idx} className={`p-4 border rounded-xl flex gap-4 ${
                                        insight.type === 'CRITICAL' ? 'bg-rose-500/5 border-rose-500/20' : 
                                        insight.type === 'POSITIVE' ? 'bg-emerald-500/5 border-emerald-500/20' : 
                                        'bg-slate-500/5 border-slate-500/10'
                                    }`}>
                                        <div className="mt-1">
                                            {insight.type === 'CRITICAL' ? <AlertCircle className="w-4 h-4 text-rose-400" /> :
                                             insight.type === 'POSITIVE' ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> :
                                             <Zap className="w-4 h-4 text-slate-400" />}
                                        </div>
                                        <div>
                                            <div className="text-[10px] font-black uppercase tracking-[0.2em] mb-1">
                                                {insight.type === 'CRITICAL' ? <span className="text-rose-400">Churn Risk Alert</span> : 
                                                 insight.type === 'POSITIVE' ? <span className="text-emerald-400">Expansion Opportunity</span> : 
                                                 <span className="text-slate-400">Market Insight</span>}
                                            </div>
                                            <p className="text-xs text-slate-400 leading-relaxed font-medium">
                                                {insight.text}
                                            </p>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="p-4 bg-slate-900/30 border border-dash border-slate-800 rounded-xl text-center text-xs text-slate-600">
                                    No notable deviations detected this week.
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="p-6 bg-[#0a0a0c] border-t border-slate-800 text-center">
                    <p className="text-[10px] text-slate-600 font-medium uppercase tracking-[0.2em]">Weekly summary generated for internal ops only</p>
                </div>
            </div>
        </div>
    );
}
